import * as Sentry from '@sentry/react';

import {
  ClerkProvider,
  // OrganizationSwitcher, // Removed as it's no longer used in this file
  useAuth as useClerkAuth,
  useOrganizationList,
} from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider, createRouter } from '@tanstack/react-router';
/// <reference types="vite/client" />
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';

// Import the generated route tree
import { routeTree } from './routeTree.gen';

import { httpBatchLink } from '@trpc/client';
import superjson from 'superjson';
import { getEnvVar, getEnvVarWithFallback, validateEnvVars } from './utils/env';
// Import tRPC setup
import { trpc } from './utils/trpc';

// Utility object for environment-aware console logging
const logger = {
  log: (...args: unknown[]) => {
    if (environment !== 'production') {
      console.log(...args);
    }
  },
  warn: (...args: unknown[]) => {
    if (environment !== 'production') {
      console.warn(...args);
    }
  },
  error: (...args: unknown[]) => {
    if (environment !== 'production') {
      console.error(...args);
    }
  },
  info: (...args: unknown[]) => {
    if (environment !== 'production') {
      console.info(...args);
    }
  },
  debug: (...args: unknown[]) => {
    if (environment !== 'production') {
      console.debug(...args);
    }
  },
};

// Validate all required environment variables
validateEnvVars({
  VITE_ENVIRONMENT: {
    required: true,
    validate: (value) => ['development', 'production', 'test'].includes(value),
    errorMessage: 'VITE_ENVIRONMENT must be one of: development, production, test',
  },
  VITE_CLERK_PUBLISHABLE_KEY: {
    required: true,
  },
  VITE_API_BASE_URL: {
    required: false,
    fallback: 'http://localhost:3001',
  },
  VITE_SENTRY_DSN: {
    required: import.meta.env.VITE_ENVIRONMENT === 'production',
    errorMessage: 'VITE_SENTRY_DSN is required in production environment',
  },
});

const environment = getEnvVar('VITE_ENVIRONMENT');

if (environment === 'production') {
  Sentry.init({
    dsn: getEnvVar('VITE_SENTRY_DSN'),
    environment: environment,
    sendDefaultPii: true,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    beforeSend(event) {
      return event;
    },
  });
} else {
  logger.log('Sentry disabled in non-production environment:', environment);
}

const PUBLISHABLE_KEY = getEnvVar('VITE_CLERK_PUBLISHABLE_KEY');
const API_BASE_URL = getEnvVarWithFallback('VITE_API_BASE_URL', 'http://localhost:3001');

const queryClient = new QueryClient();

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: `${API_BASE_URL}/api/trpc`, // Use the environment variable
      transformer: superjson,
      async headers() {
        const headers: Record<string, string> = {};
        if (typeof window !== 'undefined' && window.Clerk && window.Clerk.session) {
          const token = await window.Clerk.session.getToken();
          if (token) {
            headers.authorization = `Bearer ${token}`;
          }
        }
        return headers;
      },
    }),
  ],
});

export const useAuth = useClerkAuth;

// --- BEGIN ADDED ORGANIZATIONHANDLER COMPONENT ---
const OrganizationHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSignedIn, orgId, isLoaded: authIsLoaded } = useAuth();
  const { setActive, userMemberships, isLoaded: orgListIsLoaded } = useOrganizationList();

  const [isOrgContextResolved, setIsOrgContextResolved] = useState(false);

  useEffect(() => {
    if (!authIsLoaded || !orgListIsLoaded) {
      return;
    }

    if (!isSignedIn) {
      setIsOrgContextResolved(true);
      return;
    }

    if (orgId) {
      setIsOrgContextResolved(true);
      return;
    }

    if (userMemberships?.data?.length > 0) {
      const firstOrgMembership = userMemberships.data[0];
      if (firstOrgMembership?.organization) {
        setActive({ organization: firstOrgMembership.organization.id })
          .then(() => {
            setIsOrgContextResolved(true);
          })
          .catch((error) => {
            logger.error('Failed to set active organization:', error);
            setIsOrgContextResolved(true);
          });
      } else {
        setIsOrgContextResolved(true);
      }
    } else {
      setIsOrgContextResolved(true);
    }
  }, [authIsLoaded, orgListIsLoaded, isSignedIn, orgId, userMemberships, setActive]);

  if (!isOrgContextResolved && isSignedIn) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        Loading Organization...
      </div>
    );
  }

  return <>{children}</>;
};
// --- END ADDED ORGANIZATIONHANDLER COMPONENT ---

const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface RouterContext {
    queryClient: QueryClient;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element with ID 'root'");
}

function App() {
  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/">
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <OrganizationHandler>
            <RouterProvider router={router} />
          </OrganizationHandler>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </trpc.Provider>
    </ClerkProvider>
  );
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
